/**
 * Advertising and analytics stay denied until the visitor chooses.
 * Google Consent Mode v2 signals are derived from that choice.
 */

export type RegionPolicy = 'opt_in' | 'opt_out_outside_uk_eea';
export type KnownRegion = 'uk_eea' | 'other' | 'unknown';

export interface StoredConsent {
  essential: true;
  analytics: boolean;
  advertising: boolean;
  updatedAt: string;
  policy: RegionPolicy;
}

export type ConsentModeState = 'granted' | 'denied';

export interface ConsentModeV2 {
  analytics_storage: ConsentModeState;
  ad_storage: ConsentModeState;
  ad_user_data: ConsentModeState;
  ad_personalization: ConsentModeState;
  functionality_storage: 'granted';
  security_storage: 'granted';
}

export type ConsentAction = 'accept_all' | 'reject_optional' | 'save';

export function parseConsent(raw: string | null | undefined): StoredConsent | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredConsent>;
    if (typeof parsed.analytics !== 'boolean' || typeof parsed.advertising !== 'boolean') return null;
    if (typeof parsed.updatedAt !== 'string' || !parsed.updatedAt) return null;
    return {
      essential: true,
      analytics: parsed.analytics,
      advertising: parsed.advertising,
      updatedAt: parsed.updatedAt,
      policy: parsed.policy === 'opt_out_outside_uk_eea' ? 'opt_out_outside_uk_eea' : 'opt_in',
    };
  } catch {
    return null;
  }
}

/**
 * Without a confirmed region, optional storage stays denied.
 * The opt-out policy grants storage only when the caller already knows the
 * visitor is outside the UK and EEA. This package does not look up a region.
 */
export function defaultOptionalConsent(policy: RegionPolicy, region: KnownRegion = 'unknown'): {
  analytics: boolean;
  advertising: boolean;
} {
  if (policy === 'opt_out_outside_uk_eea' && region === 'other') {
    return { analytics: true, advertising: true };
  }
  return { analytics: false, advertising: false };
}

export function consentFromAction(
  action: ConsentAction,
  now: string,
  custom?: { analytics?: boolean; advertising?: boolean },
  policy: RegionPolicy = 'opt_in',
): StoredConsent {
  if (action === 'accept_all') {
    return { essential: true, analytics: true, advertising: true, updatedAt: now, policy };
  }
  if (action === 'reject_optional') {
    return { essential: true, analytics: false, advertising: false, updatedAt: now, policy };
  }
  return {
    essential: true,
    analytics: custom?.analytics === true,
    advertising: custom?.advertising === true,
    updatedAt: now,
    policy,
  };
}

export function consentModeV2(consent: StoredConsent | null, policy: RegionPolicy = 'opt_in', region: KnownRegion = 'unknown'): ConsentModeV2 {
  const fallback = defaultOptionalConsent(policy, region);
  const analytics = consent ? consent.analytics : fallback.analytics;
  const advertising = consent ? consent.advertising : fallback.advertising;
  return {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: advertising ? 'granted' : 'denied',
    ad_user_data: advertising ? 'granted' : 'denied',
    ad_personalization: advertising ? 'granted' : 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
  };
}

export function hasChoice(consent: StoredConsent | null): boolean {
  return Boolean(consent?.updatedAt);
}
